import { useQuery } from '@tanstack/react-query'
import { getRequestProcurementCompletion } from '../../../shared/api/procurementCompletion.api'

function formatCompletedAt(value: string | null) {
  if (!value) return null
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type CompletionRow = {
  procurementUserId: string
  procurementUserName: string
  visibleItemsTotal: number
  processedItemsTotal: number
  requestedItemsTotal?: number
  supplierRequestsTotal?: number
  supplierAnsweredTotal?: number
  supplierWaitingTotal?: number
  isCompleted: boolean
  completedAt: string | null
}

/** Раскладка прогресса в три цифры: Просчитано / Ждём / Не начато. С 21.05.2026
 *  «Просчитано» = есть реальный ответ (квота с ценой или финальный per-user state),
 *  «Ждём» = отправлен запрос поставщику без ответа (заготовка), «Не начато» =
 *  не отправлен ни одному поставщику. */
function buildStats(item: CompletionRow) {
  const total = item.visibleItemsTotal
  const processed = item.processedItemsTotal
  const requested = item.requestedItemsTotal ?? 0
  // «Ждём» — отправленные запросы, по которым ещё нет настоящего ответа.
  // requested может включать уже просчитанные позиции (если по той же
  // позиции потом пришла реальная квота) — поэтому считаем как max(0, …).
  const waiting = Math.max(0, requested - processed)
  const notStarted = Math.max(0, total - processed - waiting)
  return { total, processed, waiting, notStarted }
}

function StatusBadge({ item }: { item: CompletionRow }) {
  const { total, processed, waiting } = buildStats(item)
  if (item.isCompleted && processed > 0) {
    return (
      <span className="rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs font-medium">
        Просчитано
      </span>
    )
  }
  if (processed === 0 && waiting > 0) {
    return (
      <span className="rounded-full bg-blue-50 text-blue-700 px-2.5 py-1 text-xs font-medium">
        Запрошено у поставщиков
      </span>
    )
  }
  if (processed > 0 && processed < total) {
    return (
      <span className="rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 text-xs font-medium">
        Частично просчитано
      </span>
    )
  }
  return (
    <span className="rounded-full bg-gray-100 text-gray-600 px-2.5 py-1 text-xs font-medium">
      Не начато
    </span>
  )
}

function StatsLine({ item }: { item: CompletionRow }) {
  const { total, processed, waiting, notStarted } = buildStats(item)
  return (
    <span className="text-gray-600 tabular-nums">
      Позиции:{' '}
      <span className={processed > 0 ? 'text-emerald-700 font-medium' : 'text-gray-700'}>
        просчитано {processed}
      </span>
      <span className="mx-1.5 text-gray-300">·</span>
      <span className={waiting > 0 ? 'text-blue-700 font-medium' : 'text-gray-700'}>
        ждём {waiting}
      </span>
      <span className="mx-1.5 text-gray-300">·</span>
      <span className={notStarted > 0 ? 'text-gray-700' : 'text-gray-500'}>
        не начато {notStarted}
      </span>
      <span className="mx-1.5 text-gray-300">·</span>
      всего <span className="text-gray-800 font-medium">{total}</span>
    </span>
  )
}

function SupplierLine({ item }: { item: CompletionRow }) {
  const total = item.supplierRequestsTotal ?? 0
  if (total === 0) return null
  const answered = item.supplierAnsweredTotal ?? 0
  const waiting = item.supplierWaitingTotal ?? Math.max(0, total - answered)
  return (
    <span className="text-gray-500 text-xs tabular-nums">
      Запросы поставщикам: отправлено <span className="text-gray-800 font-medium">{total}</span>
      <span className="mx-1.5 text-gray-300">·</span>
      <span className={answered > 0 ? 'text-emerald-700 font-medium' : 'text-gray-700'}>
        ответили {answered}
      </span>
      <span className="mx-1.5 text-gray-300">·</span>
      <span className={waiting > 0 ? 'text-blue-700 font-medium' : 'text-gray-700'}>
        ждём {waiting}
      </span>
    </span>
  )
}

export function ProcurementCompletionPanel({ requestId }: { requestId: string }) {
  const { data } = useQuery({
    queryKey: ['request-procurement-completion', requestId],
    queryFn: () => getRequestProcurementCompletion(requestId),
    enabled: !!requestId,
  })

  const items = (data?.items ?? []) as CompletionRow[]
  if (items.length === 0) return null

  if (items.length === 1) {
    const item = items[0]!
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="mb-2 text-sm font-medium text-gray-700">Статус просчёта закупки</h2>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <StatusBadge item={item} />
          <span className="text-gray-500">{item.procurementUserName}</span>
          <StatsLine item={item} />
          {item.completedAt && (
            <span className="text-xs text-gray-400">{formatCompletedAt(item.completedAt)}</span>
          )}
        </div>
        <div className="mt-1.5">
          <SupplierLine item={item} />
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-medium text-gray-700">Статус просчёта закупки</h2>
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.procurementUserId}
            className="rounded-lg bg-gray-50 px-3 py-2 text-sm space-y-1"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="font-medium text-gray-800">{item.procurementUserName}</span>
              <StatsLine item={item} />
              <StatusBadge item={item} />
              {item.completedAt && (
                <span className="text-xs text-gray-400">{formatCompletedAt(item.completedAt)}</span>
              )}
            </div>
            <SupplierLine item={item} />
          </div>
        ))}
      </div>
    </div>
  )
}
